import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Zap, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const { brand } = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/");
  };

  const quickFill = (em) => {
    setEmail(em);
    setPassword(em === "jonathanvardaman@gmail.com" ? "Admin@2026" : "Demo@2026");
  };

  const accent = brand.accent_color;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0b0d12]">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="logo" className="w-10 h-10 rounded-xl object-cover bg-white" />
            ) : (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-black" style={{ background: accent }}>
                <Zap size={22} strokeWidth={2.5} />
              </div>
            )}
            <div>
              <div className="font-bold tracking-tight text-xl">{brand.company_name}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{brand.tagline}</div>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Welcome <span style={{ color: accent }}>back.</span>
          </h1>
          <p className="mt-3 text-slate-400 text-sm leading-relaxed">
            Sign in to your all-in-one HR command center — messaging, org, incidents, knowledge & onboarding.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Work email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-[#121620] border border-[#2a3447] rounded-lg px-4 py-3 outline-none transition-colors"
                style={{ borderColor: undefined }}
                onFocus={(e) => (e.currentTarget.style.borderColor = accent)}
                onBlur={(e) => (e.currentTarget.style.borderColor = "")}
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Password</label>
              <div className="relative mt-2">
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#121620] border border-[#2a3447] rounded-lg px-4 py-3 pr-11 outline-none transition-colors"
                  onFocus={(e) => (e.currentTarget.style.borderColor = accent)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "")}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="text-sm text-[#ff3b5c] bg-[#ff3b5c1a] border border-[#ff3b5c40] px-3 py-2 rounded-lg" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-60"
              style={{ background: accent }}
              onMouseEnter={(e) => (e.currentTarget.style.background = brand.accent_hover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = accent)}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#2a3447]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Quick demo accounts</div>
            <div className="flex flex-wrap gap-2">
              {[
                ["Admin", "jonathanvardaman@gmail.com"],
                ["Manager", "sarah.chen@nexuspulse.co"],
                ["Employee", "priya.rao@nexuspulse.co"],
              ].map(([label, em]) => (
                <button
                  key={em}
                  type="button"
                  onClick={() => quickFill(em)}
                  data-testid={`quick-${label.toLowerCase()}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#2a3447] text-slate-300 transition-colors hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex items-center justify-center relative overflow-hidden border-l border-[#2a3447] bg-hero-radial">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url('${brand.login_hero_url}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 p-12 max-w-lg">
          <div
            className="pill border"
            style={{
              background: `${accent}1a`,
              color: accent,
              borderColor: `${accent}40`,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }} />
            Live workspace
          </div>
          <h2 className="mt-6 text-4xl font-extrabold tracking-tight leading-tight">
            The TMHE online command center.
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Every part of running the team, in one place — HR, org hierarchy, live team communication,
            incidents, announcements, kudos, courses, and an AI-powered knowledge base. If it keeps
            TMHE moving, it lives here.
          </p>
        </div>
      </div>
    </div>
  );
}
